import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { crmApi, setAccessToken, formatApiError } from "@/orbit/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <OrbitAuthProvider>");
  return ctx;
};

export function OrbitAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [status, setStatus] = useState("checking"); // checking | authenticated | anonymous

  const applyAuth = useCallback((data) => {
    if (data?.access_token) setAccessToken(data.access_token);
    setUser(data?.user || null);
    setWorkspace(data?.workspace || null);
    setStatus(data?.user ? "authenticated" : "anonymous");
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const { data } = await crmApi.post("/auth/login", { email, password });
    applyAuth(data);
    return data;
  }, [applyAuth]);

  const register = useCallback(async (payload) => {
    const { data } = await crmApi.post("/auth/register", payload);
    applyAuth(data);
    return data;
  }, [applyAuth]);

  const acceptInvite = useCallback(async (payload) => {
    const { data } = await crmApi.post("/auth/accept-invite", payload);
    applyAuth(data);
    return data;
  }, [applyAuth]);

  const logout = useCallback(async () => {
    try {
      await crmApi.post("/auth/logout");
    } catch {
      // ignore
    }
    setAccessToken(null);
    setUser(null);
    setWorkspace(null);
    setStatus("anonymous");
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      // First try silent refresh via refresh cookie
      const { data: refreshData } = await crmApi.post("/auth/refresh");
      applyAuth(refreshData);
    } catch {
      setStatus("anonymous");
      setUser(null);
      setWorkspace(null);
    }
  }, [applyAuth]);

  useEffect(() => {
    // On mount, try silent refresh to restore session
    refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    user, workspace, status,
    login, register, acceptInvite, logout, refreshMe,
    formatApiError,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
